import {CampaignUsage} from 'common/src/modules/campaigns/campaigns.types';
import {DateTime} from 'luxon';
import {ContentFeedUpdateStatus} from 'common/src/modules/contentFeeds/contentFeeds.enums';
import {GoogleSheetDetails} from 'common/src/modules/feeds/feeds.types';
import {FeedRuleTypeName} from '../locationFeed/locationFeed.enums';
import {ScheduledJobStatus, ScheduledJobTiming} from '../jobs/jobs.types';

interface ScheduledJob {
  id: number;
  timing: ScheduledJobTiming;
  status: ScheduledJobStatus;
}

export enum ContentFeedUploadMethod {
  FileUpload = 0,
  FromUrl = 1,
  GoogleSheet = 2,
}

export enum ContentFeedUploadMethodName {
  FileUpload = 'File Upload',
  FromUrl = 'From URL',
  GoogleSheet = 'From a private google sheet',
}

interface ContentFeedMatchedField {
  taxonomyKey: string;
}

export interface RemoteContentFeed {
  id: number;
  clientId: number;
  name: string;
  itemsFileUrl: string;
  googleSheetDetails?: GoogleSheetDetails;
  uploadMethod: ContentFeedUploadMethod;
  itemsCount: number;
  createdTime: string;
  updatedTime: string;
  lastInventoryUpdateTime?: string;
  createdByUsername: string;
  updatedByUsername: string;
  usedInCampaigns: CampaignUsage[];
  matchedFields: ContentFeedMatchedField[];
  unmatchedFields: string[];
  scheduledJob?: ScheduledJob;
}

export type ContentFeedSource = FeedSourceWithFile | FeedSourceWithGoogleSheet;

export type FeedSourceWithFile = {
  uploadMethod: ContentFeedUploadMethod.FileUpload | ContentFeedUploadMethod.FromUrl;
  itemsFileUrl: string;
  googleSheetDetails?: never;
};

export type FeedSourceWithGoogleSheet = {
  uploadMethod: ContentFeedUploadMethod.GoogleSheet;
  googleSheetDetails: GoogleSheetDetails;
  itemsFileUrl?: never;
};

export type ContentFeed = Omit<
  RemoteContentFeed,
  | 'createdTime'
  | 'updatedTime'
  | 'lastInventoryUpdateTime'
  | 'uploadMethod'
  | 'itemsFileUrl'
  | 'googleSheetDetails'
> & {
  createdTime: DateTime;
  updatedTime: DateTime;
  lastInventoryUpdateTime?: DateTime;
  feedSource: ContentFeedSource;
  scheduledJob?: ScheduledJob;
};

export interface FeedUpdateJobStatisticsSection {
  messages: string[];
  counters: Record<string, number>;
}

export interface FeedUpdateJobStatistics {
  infos: FeedUpdateJobStatisticsSection;
  warnings: FeedUpdateJobStatisticsSection;
  errors: FeedUpdateJobStatisticsSection;
}

export interface RemoteContentFeedHistoryUpdate {
  id: number;
  startTime: string;
  endTime: string;
  status: ContentFeedUpdateStatus;
  message: string;
  reportUrl: string;
  statistics: FeedUpdateJobStatistics;
}

export interface ContentFeedHistoryUpdate
  extends Omit<RemoteContentFeedHistoryUpdate, 'startTime' | 'endTime'> {
  startTime: DateTime;
  endTime: DateTime;
}

export interface ContentFeedFieldMapping {
  taxonomyKeys?: string[];
  valueExamples?: string[];
  columnName: string;
}

export interface ContentFeedFieldMappingResponse {
  map: ContentFeedFieldMapping[];
}

export interface ContentFeedRule {
  taxonomyKey: string;
  rule: {
    type: FeedRuleTypeName;
  };
}
